import React, { useEffect, useMemo, useState } from 'react';
import type { Column, MatchedOptions } from './Steps/MatchColumns';
import { ColumnType } from './Steps/MatchColumns';
import type { Fields, RawData } from '../types';
import { MatchColumnSelect } from './MatchColumnSelect';
import { getFieldOptions } from './MatchColumns/getFieldOptions';
import { useRsi } from '../hooks/useRsi';
import { useTranslation } from 'react-i18next';
import { DeepReadonly } from 'ts-essentials';
import styles from './ColumnMapping.module.scss';

// ── SVG Icons ──

const ChevronIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M5 3l4 4-4 4" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M3 8h10m0 0l-3-3m3 3l-3 3" />
  </svg>
);

const SmallArrowIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M2 6h8m0 0l-2-2m2 2l-2 2" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M2 6.5l2.5 2.5L10 4" />
  </svg>
);

const CircleIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="6" cy="6" r="4" />
  </svg>
);

const CrossIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M3 3l6 6m0-6l-6 6" />
  </svg>
);

const IgnoreXIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M4 4l6 6m0-6l-6 6" />
  </svg>
);

const RestoreIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M2 7h10" /><path d="M8 3l4 4-4 4" />
  </svg>
);

// ── Helper: Excel column letter ──

const getColumnLetter = (index: number): string => {
  let letter = '';
  let temp = index;
  while (temp >= 0) {
    letter = String.fromCharCode(65 + (temp % 26)) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
};

// ── Helper: combine class names ──

const cn = (...classes: (string | false | undefined | null)[]): string => classes.filter(Boolean).join(' ');

// ── Value match row styles for react-select ──

const valSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: 28,
    borderRadius: '1rem',
    border: state.isFocused ? '1px solid #16CCC3' : '1px solid #E8E8E8',
    boxShadow: 'none',
    ':hover': { borderColor: state.isFocused ? '#16CCC3' : '#E8E8E8' },
  }),
  valueContainer: (provided: any) => ({ ...provided, padding: '0 6px', fontSize: 11 }),
  input: (provided: any) => ({ ...provided, margin: 0, padding: 0, fontSize: 11 }),
  indicatorsContainer: (provided: any) => ({ ...provided, height: 26 }),
  menuList: (provided: any) => ({ ...provided, scrollbarWidth: 'thin', scrollbarColor: '#1781A1 #fff' }),
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: 11,
    padding: '4px 8px',
    color: state.isSelected ? '#fff' : '#000',
    backgroundColor: state.isSelected ? '#1781A1' : '#fff',
    ':hover': { backgroundColor: '#E5EFF6', color: '#000' },
  }),
};

// ── Card select styles for react-select ──

const cardSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: 32,
    borderRadius: '1rem',
    border: state.isFocused ? '1px solid #16CCC3' : '1px solid #E8E8E8',
    boxShadow: 'none',
    ':hover': { borderColor: state.isFocused ? '#16CCC3' : '#E8E8E8' },
  }),
  valueContainer: (provided: any) => ({ ...provided, padding: '0 8px' }),
  input: (provided: any) => ({ ...provided, margin: 0, padding: 0 }),
  indicatorsContainer: (provided: any) => ({ ...provided, height: 30 }),
  menuList: (provided: any) => ({ ...provided, scrollbarWidth: 'thin', scrollbarColor: '#1781A1 #fff' }),
  option: (provided: any, state: any) => ({
    ...provided,
    color: state.isSelected ? '#fff' : '#000',
    backgroundColor: state.isSelected ? '#1781A1' : '#fff',
    ':hover': { backgroundColor: '#E5EFF6', color: '#000' },
  }),
};

// ── ValueMatchRow component ──

interface ValueMatchRowProps<T extends string> {
  option: MatchedOptions<T> | Partial<MatchedOptions<T>>;
  columnValue: T;
  columnIndex: number;
  onSubChange: (val: string, isFuelType: boolean, index: number, entry: string) => void;
}

const ValueMatchRow = <T extends string>({ option, columnValue, columnIndex, onSubChange }: ValueMatchRowProps<T>) => {
  const { fields } = useRsi<T>();
  const { t } = useTranslation();
  const options = getFieldOptions(fields, columnValue);

  const currentSelection = useMemo(
    () => options.find((opt: any) => opt.value == option.value),
    [options, option.value],
  );

  const handleChange = (selected: any) => {
    onSubChange(selected?.value ?? '', selected?.isFuelType ?? false, columnIndex, option.entry!);
  };

  return (
    <div className={styles.valRow}>
      <span className={styles.valFrom} title={option.entry}>{option.entry}</span>
      <span className={styles.valArrow}><SmallArrowIcon /></span>
      <div className={styles.valToWrap}>
        <MatchColumnSelect
          value={currentSelection}
          placeholder={t('spreadsheet_import.match_columns_step.sub_select_placeholder') as string}
          onChange={handleChange}
          options={options}
          name={option.entry}
          isRequired={true}
          styles={valSelectStyles}
        />
      </div>
    </div>
  );
};

// ── ColumnCard component ──

interface ColumnCardProps<T extends string> {
  column: Column<T>;
  dataExample: RawData[];
  fields: Fields<T>;
  selectedOptions: { value: DeepReadonly<T>; label: string }[];
  onChange: (value: T, columnIndex: number) => void;
  onSubChange: (value: string, isFuelType: boolean, columnIndex: number, entry: string) => void;
  onIgnore: (columnIndex: number) => void;
  onRevertIgnore: (columnIndex: number) => void;
}

export const ColumnCard = <T extends string>({
  column,
  dataExample,
  fields,
  selectedOptions,
  onChange,
  onSubChange,
  onIgnore,
  onRevertIgnore,
}: ColumnCardProps<T>) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const isIgnored = column.type === ColumnType.ignored;
  const isEmpty = column.type === ColumnType.empty;
  const isMatched = !isEmpty && !isIgnored;

  const isFullyMatched =
    column.type === ColumnType.matched ||
    column.type === ColumnType.matchedCheckbox ||
    column.type === ColumnType.matchedSelectOptions;

  const hasValues = 'matchedOptions' in column && !!column.matchedOptions?.length;

  const field = fields.find(f => f && ('value' in column && column.value === f.key));

  // Available target field options (current + unused)
  const selectOptions = fields
    .filter(f => f && (('value' in column && column.value === f.key) || (selectedOptions.findIndex(so => so.value === f.key) < 0)))
    .map(({ label, key }) => ({ value: key, label }));

  const selectValue = selectOptions.find(({ value }) => 'value' in column && column.value === value);

  // Column letter (A, B, C, ..., Z, AA, AB, ...)
  const columnLetter = getColumnLetter(column.index);

  // Sample data preview from first 2 data rows
  const sampleText = useMemo(() => {
    const samples = dataExample
      .map(row => row[column.index])
      .filter(Boolean);
    return samples.length > 0 ? samples.join(', ') + ' …' : '';
  }, [dataExample, column.index]);

  // Card CSS class: green=fully matched, amber=partially matched, gray=unmatched
  const cardClass = cn(
    styles.colCard,
    isIgnored ? styles.ignored : isFullyMatched ? styles.matched : isMatched ? styles.partiallyMatched : styles.unmatched,
  );

  // Status indicator
  const getStatusClass = () => {
    if (isIgnored) return styles.statusOff;
    if (isFullyMatched) return styles.statusOk;
    if (isMatched && !isFullyMatched) return styles.statusWarn;
    return styles.statusEmpty;
  };

  const getStatusIcon = () => {
    if (isIgnored) return <CrossIcon />;
    if (isFullyMatched) return <CheckIcon />;
    return <CircleIcon />;
  };

  // Visible matched options (respecting onlyOneOption)
  const visibleMatchedOptions = useMemo(() => {
    if (!hasValues || !('matchedOptions' in column)) return [];
    const opts = column.matchedOptions || [];
    if (field?.onlyOneOption) {
      return opts.filter((x, i) => !!x.entry && i === 0);
    }
    return opts.filter(x => !!x.entry);
  }, [column, hasValues, field]);

  const isRequired = field?.validations?.some(v => v.rule === 'required') ?? false;

  useEffect(() => {
    if (hasValues && !isIgnored) {
      setExpanded(true);
    }
  }, [hasValues, isIgnored]);

  const handleIgnore = () => {
    if (isIgnored) {
      onRevertIgnore(column.index);
    } else {
      onIgnore(column.index);
      setExpanded(false);
    }
  };

  const toggleExpand = () => {
    if (hasValues && !isIgnored) {
      setExpanded(prev => !prev);
    }
  };

  return (
    <div className={cardClass} style={{ animationDelay: `${column.index * 0.02}s` }}>
      <div className={styles.colMain}>
        {/* Expand chevron */}
        <button
          className={cn(
            styles.colExpand,
            expanded && styles.colExpandOpen,
            (!hasValues || isIgnored) && styles.colExpandDisabled,
          )}
          onClick={toggleExpand}
          type="button"
        >
          <ChevronIcon />
        </button>

        {/* Source column info */}
        <div className={styles.colSource}>
          <span className={styles.colSourceName}>
            <span className={styles.colLetter} title={t('spreadsheet_import.match_columns_step.column_tooltip') as string}>{columnLetter}</span>
            {column.header}
          </span>
          {sampleText && <span className={styles.colSample} title={sampleText}>{sampleText}</span>}
        </div>

        {/* Arrow */}
        <span className={styles.colArrow}><ArrowIcon /></span>

        {/* Target field select or ignored label */}
        {isIgnored ? (
          <span className={styles.colIgnoredLabel}>
            {t('spreadsheet_import.match_columns_step.ignored_column_text') as string}
          </span>
        ) : (
          <div className={styles.colTargetWrap}>
            <MatchColumnSelect
              placeholder={t('spreadsheet_import.match_columns_step.select_placeholder') as string}
              value={selectValue}
              onChange={value => onChange(value?.value as T, column.index)}
              options={selectOptions}
              name={column.header}
              styles={cardSelectStyles}
            />
          </div>
        )}

        {/* Status + Ignore/Restore */}
        <div className={styles.colActions}>
          <div className={cn(styles.colStatus, getStatusClass())}>
            {getStatusIcon()}
          </div>
          <button
            className={cn(styles.colIgnore, isIgnored && styles.colIgnoreActive)}
            onClick={handleIgnore}
            type="button"
            disabled={isRequired && !isIgnored}
            title={isIgnored
              ? t('import_charges.undo_ignore_column') as string
              : t('import_charges.ignore_column') as string
            }
          >
            {isIgnored ? <RestoreIcon /> : <IgnoreXIcon />}
          </button>
        </div>
      </div>

      {/* Expandable value matching panel */}
      {hasValues && expanded && !isIgnored && 'value' in column && visibleMatchedOptions.length > 0 && (
        <div className={styles.colValues}>
          <div className={styles.colValuesTitle}>
            {t('spreadsheet_import.match_columns_step.value_matching_title') as string}
          </div>
          {visibleMatchedOptions.map(option => (
            <ValueMatchRow
              key={option.entry}
              option={option}
              columnValue={column.value}
              columnIndex={column.index}
              onSubChange={onSubChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};
